import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero, Reveal, SectionTag } from "@/components/ui-bits";
import api from "@/lib/api";

const DATA_AI_CARD = {
  slug: "data-ai",
  name: "Data & AI",
  tagline: "Turn Data Into Intelligence. Turn AI Into Business Impact.",
  summary: "Transform enterprise data into intelligent applications, analytics and AI-powered automation.",
  capabilities: ["Generative AI", "Machine Learning", "Data Engineering", "Analytics & BI"],
  isDataAI: true,
};

const Services = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => {
      const docs = r.data;
      const merged = [...docs];
      merged.splice(2, 0, DATA_AI_CARD);
      setServices(merged);
    }).catch(() => setServices([DATA_AI_CARD]));
  }, []);

  return (
    <>
      <PageHero
        eyebrow="Services — What TARK does"
        lines={["Engineering Services,", "Defined Precisely."]}
        sub="Eight service pillars covering cloud, DevOps, data & AI, software, enterprise applications, digital transformation, quality and managed services — each with clear scope, technologies and outcomes."
      />
      <section className="mx-auto max-w-7xl px-6 pb-28 lg:px-10" data-testid="services-grid">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 3) * 0.07}>
              <Link
                to={s.isDataAI ? "/data-ai" : `/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className={`card-glow group flex h-full flex-col rounded-2xl border p-8 ${
                  s.isDataAI ? "border-tark-blue/40 bg-gradient-to-br from-tark-card to-[#0A1A33]" : "border-tark-line bg-tark-card"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <h3 className={`font-display text-xl font-bold tracking-tight ${s.isDataAI ? "text-tark-cyan" : ""}`}>{s.name}</h3>
                  <ArrowUpRight className="h-5 w-5 shrink-0 text-tark-dim transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-tark-cyan" />
                </div>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-tark-dim">{s.tagline}</p>
                <p className="mt-4 text-sm leading-relaxed text-tark-mut">{s.summary}</p>
                {s.capabilities?.length > 0 && (
                  <ul className="mt-5 flex flex-wrap gap-2">
                    {s.capabilities.slice(0, 4).map((c) => (
                      <li key={c} className="rounded border border-tark-line/80 bg-tark-bg/60 px-2.5 py-1 font-mono text-[11px] text-tark-dim">{c}</li>
                    ))}
                  </ul>
                )}
                <span className="mt-auto pt-6 font-display text-xs font-bold uppercase tracking-[0.2em] text-tark-blue group-hover:text-tark-cyan">
                  Learn More
                </span>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-20 rounded-2xl border border-tark-line bg-tark-surface/60 p-10 text-center">
          <SectionTag>Not sure where to start?</SectionTag>
          <h2 className="mt-4 font-display text-2xl font-bold sm:text-3xl">Discuss Your Project</h2>
          <p className="mx-auto mt-4 max-w-xl text-sm text-tark-mut">
            Tell us what you are trying to achieve. We will map it to the right engineering capabilities.
          </p>
          <div className="mt-8">
            <Link to="/start-a-project" data-testid="services-page-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-tark-blue px-8 py-3.5 font-display text-sm font-bold text-white transition-colors hover:bg-tark-vivid">
              Request Consultation <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
};

export default Services;
